/**
 * AccountsManagement Component
 *
 * Super-admin section for managing all tenant accounts:
 * - List all accounts with status, plan, storage usage
 * - Create new accounts manually
 * - Modify plan/quota/status
 * - Suspend/reactivate accounts
 */

import React, { useState, useMemo } from 'react';
import {
  Building2,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Globe,
  HardDrive,
  Users,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  MoreHorizontal,
  Trash2,
  Edit2,
  Power,
  PowerOff,
  X,
  Check,
  Info,
} from 'lucide-react';
import { useTranslation } from '../../hooks';
import type {
  Account,
  AccountOverview,
  AccountStatus,
  SubscriptionPlan,
  CreateAccountInput,
} from '@business-assistant/shared/domain/account';
import {
  formatBytes,
  getStatusInfo,
  getPlanDisplayName,
  generateSlug,
  validateSlugFormat,
} from '@business-assistant/shared/domain/account';
import './AccountsManagement.css';

// =============================================================================
// TYPES
// =============================================================================

interface AccountsManagementProps {
  // No props - uses internal API calls
}

// Mock data for now (until backend is ready)
const MOCK_ACCOUNTS: AccountOverview[] = [
  {
    id: 'acc-001' as any,
    name: 'Acme Corporation',
    slug: 'acme-corp',
    schemaName: 'acc_001',
    ownerId: 'usr-001',
    status: 'active',
    plan: 'professional',
    storageQuotaBytes: 10737418240,
    storageUsedBytes: 3221225472,
    maxUsers: 10,
    currentUsers: 5,
    billingEmail: 'billing@acme.com',
    createdAt: '2025-06-15T10:00:00Z',
    updatedAt: '2026-01-30T14:30:00Z',
    ownerName: 'John Doe',
    ownerEmail: 'john@acme.com',
    primaryDomain: 'acme-corp.ba.teknology.eu',
    subscriptionPlan: 'professional',
    subscriptionStatus: 'active',
    storageUsagePercent: 30,
  },
  {
    id: 'acc-002' as any,
    name: 'TechStart SRL',
    slug: 'techstart',
    schemaName: 'acc_002',
    ownerId: 'usr-002',
    status: 'trial',
    plan: 'starter',
    storageQuotaBytes: 1073741824,
    storageUsedBytes: 107374182,
    maxUsers: 3,
    currentUsers: 2,
    billingEmail: 'admin@techstart.it',
    createdAt: '2026-01-20T09:00:00Z',
    updatedAt: '2026-01-28T16:45:00Z',
    trialEndsAt: '2026-02-20T09:00:00Z',
    ownerName: 'Maria Rossi',
    ownerEmail: 'maria@techstart.it',
    primaryDomain: 'techstart.ba.teknology.eu',
    subscriptionPlan: 'starter',
    subscriptionStatus: 'trialing',
    storageUsagePercent: 10,
  },
  {
    id: 'acc-003' as any,
    name: 'Global Industries',
    slug: 'global-ind',
    schemaName: 'acc_003',
    ownerId: 'usr-003',
    status: 'suspended',
    plan: 'enterprise',
    storageQuotaBytes: 107374182400,
    storageUsedBytes: 53687091200,
    maxUsers: 50,
    currentUsers: 35,
    billingEmail: 'accounts@global-ind.com',
    createdAt: '2024-12-01T08:00:00Z',
    updatedAt: '2026-01-15T11:20:00Z',
    suspendedAt: '2026-01-15T11:20:00Z',
    ownerName: 'Roberto Bianchi',
    ownerEmail: 'r.bianchi@global-ind.com',
    primaryDomain: 'global-ind.ba.teknology.eu',
    customDomain: 'app.global-industries.com',
    subscriptionPlan: 'enterprise',
    subscriptionStatus: 'past_due',
    storageUsagePercent: 50,
  },
];

// =============================================================================
// STATUS & PLAN BADGES
// =============================================================================

function StatusBadge({ status }: { status: AccountStatus }) {
  const { t } = useTranslation();
  const info = getStatusInfo(status);

  const colorClass = {
    green: 'badge-success',
    yellow: 'badge-warning',
    red: 'badge-error',
    gray: 'badge-neutral',
  }[info.color];

  const icons = {
    active: <CheckCircle size={12} />,
    trial: <Clock size={12} />,
    suspended: <AlertCircle size={12} />,
    cancelled: <XCircle size={12} />,
    pending: <Clock size={12} />,
  };

  return (
    <span className={`status-badge ${colorClass}`}>
      {icons[status]}
      {t(`accounts.status.${status}`)}
    </span>
  );
}

function PlanBadge({ plan }: { plan: SubscriptionPlan }) {
  const { t } = useTranslation();

  const colorClass = {
    starter: 'badge-neutral',
    professional: 'badge-info',
    enterprise: 'badge-primary',
    custom: 'badge-purple',
  }[plan];

  return (
    <span className={`plan-badge ${colorClass}`}>
      {t(`accounts.plans.${plan}`)}
    </span>
  );
}

// =============================================================================
// STORAGE BAR
// =============================================================================

function StorageBar({ usedBytes, quotaBytes }: { usedBytes: number; quotaBytes: number }) {
  const percent = quotaBytes > 0 ? (usedBytes / quotaBytes) * 100 : 0;
  const barClass = percent >= 90 ? 'bar-critical' : percent >= 70 ? 'bar-warning' : 'bar-normal';

  return (
    <div className="storage-bar-container">
      <div className="storage-bar">
        <div
          className={`storage-bar-fill ${barClass}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      <span className="storage-text">
        {formatBytes(usedBytes)} / {formatBytes(quotaBytes)}
      </span>
    </div>
  );
}

// =============================================================================
// ACCOUNT ROW
// =============================================================================

interface AccountRowProps {
  account: AccountOverview;
  onEdit: (account: AccountOverview) => void;
  onSuspend: (account: AccountOverview) => void;
  onActivate: (account: AccountOverview) => void;
  onDelete: (account: AccountOverview) => void;
  onViewDetails: (account: AccountOverview) => void;
}

function AccountRow({
  account,
  onEdit,
  onSuspend,
  onActivate,
  onDelete,
  onViewDetails,
}: AccountRowProps) {
  const { t, language } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <tr className={account.status === 'suspended' ? 'row-suspended' : ''}>
      <td>
        <div className="account-name-cell">
          <Building2 size={16} className="account-icon" />
          <div>
            <div className="account-name">{account.name}</div>
            <div className="account-slug">{account.slug}.ba.teknology.eu</div>
          </div>
        </div>
      </td>
      <td>
        <div className="owner-cell">
          <div className="owner-name">{account.ownerName || '-'}</div>
          <div className="owner-email">{account.ownerEmail}</div>
        </div>
      </td>
      <td>
        <PlanBadge plan={account.plan} />
      </td>
      <td>
        <StatusBadge status={account.status} />
      </td>
      <td>
        <StorageBar usedBytes={account.storageUsedBytes} quotaBytes={account.storageQuotaBytes} />
      </td>
      <td>
        <div className="users-cell">
          <Users size={14} />
          <span>
            {account.currentUsers} / {account.maxUsers}
          </span>
        </div>
      </td>
      <td className="date-cell">{formatDate(account.createdAt)}</td>
      <td>
        <div className="actions-cell">
          <button
            className="action-btn"
            onClick={() => onViewDetails(account)}
            title={t('accounts.actions.viewDetails')}
          >
            <ExternalLink size={14} />
          </button>
          <div className="action-menu-container">
            <button
              className="action-btn"
              onClick={() => setShowMenu(!showMenu)}
              title={t('common.more')}
            >
              <MoreHorizontal size={14} />
            </button>
            {showMenu && (
              <>
                <div className="menu-backdrop" onClick={() => setShowMenu(false)} />
                <div className="action-menu">
                  <button onClick={() => { onEdit(account); setShowMenu(false); }}>
                    <Edit2 size={14} />
                    {t('accounts.actions.edit')}
                  </button>
                  {account.status === 'active' || account.status === 'trial' ? (
                    <button onClick={() => { onSuspend(account); setShowMenu(false); }}>
                      <PowerOff size={14} />
                      {t('accounts.actions.suspend')}
                    </button>
                  ) : account.status === 'suspended' ? (
                    <button onClick={() => { onActivate(account); setShowMenu(false); }}>
                      <Power size={14} />
                      {t('accounts.actions.activate')}
                    </button>
                  ) : null}
                  <button
                    className="action-danger"
                    onClick={() => { onDelete(account); setShowMenu(false); }}
                  >
                    <Trash2 size={14} />
                    {t('accounts.actions.delete')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// =============================================================================
// CREATE ACCOUNT MODAL
// =============================================================================

interface CreateAccountModalProps {
  onClose: () => void;
  onSubmit: (data: CreateAccountInput) => void;
}

function CreateAccountModal({ onClose, onSubmit }: CreateAccountModalProps) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [plan, setPlan] = useState<SubscriptionPlan>('starter');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [autoSlug, setAutoSlug] = useState(true);

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    setName(value);
    if (autoSlug) {
      setSlug(generateSlug(value));
    }
  };

  // Validate slug
  const handleSlugChange = (value: string) => {
    setAutoSlug(false);
    setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
    const validation = validateSlugFormat(value);
    setSlugError(validation.valid ? null : validation.error || null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim() || !billingEmail.trim() || slugError) return;

    onSubmit({
      name: name.trim(),
      slug: slug.trim(),
      billingEmail: billingEmail.trim(),
      plan,
    });
  };

  const isValid = name.trim() && slug.trim() && billingEmail.trim() && !slugError;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-medium" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <Building2 size={18} />
            {t('accounts.modal.createTitle')}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Account Name */}
            <div className="form-field">
              <label className="form-label">
                {t('accounts.modal.nameLabel')} <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder={t('accounts.modal.namePlaceholder')}
                required
              />
            </div>

            {/* Slug */}
            <div className="form-field">
              <label className="form-label">
                {t('accounts.modal.slugLabel')} <span className="required">*</span>
              </label>
              <div className="slug-input-container">
                <input
                  type="text"
                  className={`form-input ${slugError ? 'input-error' : ''}`}
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="acme-corp"
                  required
                />
                <span className="slug-suffix">.ba.teknology.eu</span>
              </div>
              {slugError && <p className="form-error">{slugError}</p>}
              <p className="form-hint">
                <Info size={12} />
                {t('accounts.modal.slugHint')}
              </p>
            </div>

            {/* Billing Email */}
            <div className="form-field">
              <label className="form-label">
                {t('accounts.modal.billingEmailLabel')} <span className="required">*</span>
              </label>
              <input
                type="email"
                className="form-input"
                value={billingEmail}
                onChange={(e) => setBillingEmail(e.target.value)}
                placeholder="billing@company.com"
                required
              />
            </div>

            {/* Plan */}
            <div className="form-field">
              <label className="form-label">{t('accounts.modal.planLabel')}</label>
              <select
                className="form-select"
                value={plan}
                onChange={(e) => setPlan(e.target.value as SubscriptionPlan)}
              >
                <option value="starter">{t('accounts.plans.starter')}</option>
                <option value="professional">{t('accounts.plans.professional')}</option>
                <option value="enterprise">{t('accounts.plans.enterprise')}</option>
                <option value="custom">{t('accounts.plans.custom')}</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={!isValid}>
              <Plus size={14} />
              {t('accounts.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function AccountsManagement(_props: AccountsManagementProps) {
  const { t } = useTranslation();

  // State
  const [accounts] = useState<AccountOverview[]>(MOCK_ACCOUNTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<AccountStatus | 'all'>('all');
  const [planFilter, setPlanFilter] = useState<SubscriptionPlan | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filtered accounts
  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          account.name.toLowerCase().includes(query) ||
          account.slug.toLowerCase().includes(query) ||
          account.ownerEmail?.toLowerCase().includes(query) ||
          account.ownerName?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && account.status !== statusFilter) {
        return false;
      }

      // Plan filter
      if (planFilter !== 'all' && account.plan !== planFilter) {
        return false;
      }

      return true;
    });
  }, [accounts, searchQuery, statusFilter, planFilter]);

  // Stats
  const stats = useMemo(() => {
    const total = accounts.length;
    const active = accounts.filter((a) => a.status === 'active').length;
    const trial = accounts.filter((a) => a.status === 'trial').length;
    const suspended = accounts.filter((a) => a.status === 'suspended').length;
    return { total, active, trial, suspended };
  }, [accounts]);

  // Handlers
  const handleCreateAccount = (data: CreateAccountInput) => {
    console.log('Creating account:', data);
    // TODO: Call accountService.createAccount(data)
    setShowCreateModal(false);
  };

  const handleEditAccount = (account: AccountOverview) => {
    console.log('Edit account:', account.id);
    // TODO: Open edit modal
  };

  const handleSuspendAccount = (account: AccountOverview) => {
    console.log('Suspend account:', account.id);
    // TODO: Call accountService.suspendAccount(account.id)
  };

  const handleActivateAccount = (account: AccountOverview) => {
    console.log('Activate account:', account.id);
    // TODO: Call accountService.activateAccount(account.id)
  };

  const handleDeleteAccount = (account: AccountOverview) => {
    console.log('Delete account:', account.id);
    // TODO: Confirm and call accountService.deleteAccount(account.id)
  };

  const handleViewDetails = (account: AccountOverview) => {
    console.log('View details:', account.id);
    // TODO: Navigate to AccountDetail view
  };

  return (
    <div className="accounts-management">
      {/* Header */}
      <div className="accounts-header">
        <div className="accounts-title">
          <Building2 size={20} />
          <h2>{t('accounts.title')}</h2>
        </div>
        <div className="accounts-stats">
          <span className="stat">
            <strong>{stats.total}</strong> {t('accounts.stats.total')}
          </span>
          <span className="stat-separator">|</span>
          <span className="stat stat-success">
            <CheckCircle size={12} />
            <strong>{stats.active}</strong> {t('accounts.stats.active')}
          </span>
          <span className="stat stat-warning">
            <Clock size={12} />
            <strong>{stats.trial}</strong> {t('accounts.stats.trial')}
          </span>
          <span className="stat stat-error">
            <AlertCircle size={12} />
            <strong>{stats.suspended}</strong> {t('accounts.stats.suspended')}
          </span>
        </div>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={16} />
          {t('accounts.actions.createAccount')}
        </button>
      </div>

      {/* Filters Bar */}
      <div className="accounts-filters">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder={t('accounts.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          className={`filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={16} />
          {t('accounts.filters.toggle')}
          {showFilters ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      {/* Filter Options */}
      {showFilters && (
        <div className="filter-options">
          <div className="filter-group">
            <label>{t('accounts.filters.status')}</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AccountStatus | 'all')}
            >
              <option value="all">{t('accounts.filters.all')}</option>
              <option value="active">{t('accounts.status.active')}</option>
              <option value="trial">{t('accounts.status.trial')}</option>
              <option value="suspended">{t('accounts.status.suspended')}</option>
              <option value="cancelled">{t('accounts.status.cancelled')}</option>
            </select>
          </div>
          <div className="filter-group">
            <label>{t('accounts.filters.plan')}</label>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value as SubscriptionPlan | 'all')}
            >
              <option value="all">{t('accounts.filters.all')}</option>
              <option value="starter">{t('accounts.plans.starter')}</option>
              <option value="professional">{t('accounts.plans.professional')}</option>
              <option value="enterprise">{t('accounts.plans.enterprise')}</option>
              <option value="custom">{t('accounts.plans.custom')}</option>
            </select>
          </div>
          {(statusFilter !== 'all' || planFilter !== 'all') && (
            <button
              className="clear-filters"
              onClick={() => {
                setStatusFilter('all');
                setPlanFilter('all');
              }}
            >
              <X size={14} />
              {t('accounts.filters.clear')}
            </button>
          )}
        </div>
      )}

      {/* Accounts Table */}
      <div className="accounts-table-container">
        <table className="accounts-table">
          <thead>
            <tr>
              <th>{t('accounts.table.account')}</th>
              <th>{t('accounts.table.owner')}</th>
              <th>{t('accounts.table.plan')}</th>
              <th>{t('accounts.table.status')}</th>
              <th>{t('accounts.table.storage')}</th>
              <th>{t('accounts.table.users')}</th>
              <th>{t('accounts.table.created')}</th>
              <th>{t('accounts.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  <Building2 size={32} />
                  <p>{t('accounts.empty.noResults')}</p>
                </td>
              </tr>
            ) : (
              filteredAccounts.map((account) => (
                <AccountRow
                  key={account.id}
                  account={account}
                  onEdit={handleEditAccount}
                  onSuspend={handleSuspendAccount}
                  onActivate={handleActivateAccount}
                  onDelete={handleDeleteAccount}
                  onViewDetails={handleViewDetails}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Account Modal */}
      {showCreateModal && (
        <CreateAccountModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateAccount}
        />
      )}
    </div>
  );
}

export default AccountsManagement;
